import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Editor } from '@tiptap/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTemplates } from '../hooks/useTemplate'
import { NONE_MERCHANT, useMerchantContext } from './merchant-context'
import { useProcessorContext } from './processor-context'
import {
  EvidenceRule,
  JSONConfig,
  TemplateDTO,
  TemplateType
} from '../dto/template'
import {
  callOverrideTemplate,
  createNewTemplate,
  deleteTemplate,
  generateJsonLogic,
  postEvidenceRules,
  patchToggleActive,
  putTemplate,
  changeTemplateHiddenStatus,
  changeTemplateRenderingExcludedStatus
} from '../utils/template-utils'
import { useRelevantTemplates, useTemplate } from '../useTemplateTooling'
import { useJsonConfig } from '../hooks/useJsonConfig'
import { postprocessContentForBackend } from '../template-editor-components/editor/utils/processContent'
import { useDataFieldContext } from './data-field-context'

export const ACTIVE_TEMPLATE_PARAM_NAME = 'template'

export interface EditorState {
  editor: Editor | null
  rawHtmlContent?: string | null
  isDirty: boolean
}

interface ITemplateContext {
  templateList?: TemplateDTO[]
  rootTemplates?: TemplateDTO[]
  activeTemplateData?: TemplateDTO
  gpdTemplateData?: TemplateDTO
  templatesHash?: Map<number, TemplateDTO>
  activeTemplate?: number
  setActiveTemplate?: (id: number) => void
  collapsedState?: boolean
  toggleCollapse?: () => void
  isTemplatesLoading?: boolean
  searchValue: string
  setSearchValue: (value: string) => void
  selectTemplate: (id: number, parentList: string) => void
  parentList: string // list of template ids divided with '-'
  createTemplate: (
    originTemplate: TemplateDTO,
    newTemplateData: Partial<TemplateDTO>
  ) => void
  updatedEvidenceRules: EvidenceRule
  setUpdatedEvidenceRules: (data: EvidenceRule) => void
  saveTemplateChanges: () => Promise<void>
  removeTemplate: (id: number) => Promise<void>
  overrideTemplate: (template: TemplateDTO) => Promise<void>
  isLoading: boolean
  generateRules: (description: string) => Promise<Record<string, unknown>>
  saveEvidenceRules: (rules: EvidenceRule, isNew: boolean) => Promise<void>
  toggleTemplateHiddenStatus: (id: number) => Promise<void>
  toggleTemplateRenderingExcludedStatus: (id: number) => Promise<void>
  updatedTemplateName: string | null
  setUpdatedTemplateName: (name: string | null) => void
  onRevertOverride: () => void
  jsonConfig: JSONConfig
  relevantTemplates: Record<number, TemplateDTO[]>
  updatedTemplateContent: EditorState
  setUpdatedTemplateContent: Dispatch<SetStateAction<EditorState>>
  onToggleActiveStatus: () => void
}

const TemplateContext = createContext<ITemplateContext>({})

export const TemplateProvider = ({ children }) => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const searchParams = new URLSearchParams(search)
  const activeTemplateParamId = searchParams.get(ACTIVE_TEMPLATE_PARAM_NAME)
  const { selectedMerchant } = useMerchantContext()
  const {
    selectedProcessor,
    isProcessorsLoading,
    isAddingProcessorField,
    newProcessorId
  } = useProcessorContext()
  const { addedDataFields, resetAddedDataFields, refetchDataFields } =
    useDataFieldContext()
  const { templatesData, isTemplatesLoading, refetchTemplates } = useTemplates(
    selectedMerchant?.id,
    selectedProcessor?.id
  )
  const { jsonConfig, isJsonConfigLoading } = useJsonConfig(
    selectedMerchant?.id
  )
  const [rootTemplates, setRootTemplates] = useState([])
  const [activeTemplate, setActiveTemplate] = useState<number | null>(null)
  const [gpdTemplateId, setGpdTemplateId] = useState<number | null>(null)
  const [collapsedState, setCollapsedState] = useState<boolean>(false)
  const {
    templateData: activeTemplateData,
    isTemplateLoading,
    refetchTemplate
  } = useTemplate(activeTemplate)
  const {
    templateData: gpdTemplateData,
    isTemplateLoading: isGpdTemplateLoading
  } = useTemplate(gpdTemplateId)
  const {
    relevantTemplates: activeRelevantTemplates,
    isRelevantTemplateLoading,
    refetchRelevantTemplate
  } = useRelevantTemplates(activeTemplate)
  const {
    relevantTemplates: gpdRelevantTemplates,
    isRelevantTemplateLoading: isGpdRelevantTemplateLoading,
    refetchRelevantTemplate: refetchGpdRelevantTemplate
  } = useRelevantTemplates(gpdTemplateId)
  const [parentList, setParentList] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [templatesHash, setTemplatesHash] = useState<Map<number, TemplateDTO>>(
    new Map()
  )
  const templatesHashRef = useRef(templatesHash)
  const [updatedEvidenceRules, setUpdatedEvidenceRules] =
    useState<EvidenceRule | null>(null)
  const [isGeneralLoading, setIsGeneralLoading] = useState(false)
  const [updatedTemplateContent, setUpdatedTemplateContent] =
    useState<EditorState>({
      editor: null,
      rawHtmlContent: null,
      isDirty: false
    })
  const [updatedTemplateName, setUpdatedTemplateName] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (templatesData) {
      const filteredTemplates = templatesData.filter(template => {
        return (
          !template.parentTemplateIds?.length ||
          template.templateType === TemplateType.configurationPanel
        )
      })
      setRootTemplates(filteredTemplates)
    } else {
      setRootTemplates([])
    }
  }, [templatesData])

  useEffect(() => {
    if (templatesData) {
      const hash = new Map()
      templatesData.forEach(template => {
        hash.set(template.templateId, template)
      })
      templatesHashRef.current = hash
      setTemplatesHash(hash)
    } else {
      const hash = new Map()
      templatesHashRef.current = hash
      setTemplatesHash(hash)
    }
  }, [templatesData])

  useEffect(() => {
    setActiveTemplate(null)
    setGpdTemplateId(null)
  }, [templatesData])

  const selectTemplate = (id: number | null, parentList: string) => {
    if (id === activeTemplate) {
      return
    }
    setUpdatedTemplateContent({
      editor: null,
      rawHtmlContent: null,
      isDirty: false
    })
    setUpdatedTemplateName(null)
    setActiveTemplate(id)
    setParentList(parentList)
    setUpdatedEvidenceRules(null)

    const searchParams = new URLSearchParams(search)
    if (id) {
      searchParams.set(ACTIVE_TEMPLATE_PARAM_NAME, `${id}`)
    } else {
      searchParams.delete(ACTIVE_TEMPLATE_PARAM_NAME)
    }
    navigate({ pathname, search: searchParams.toString() })

    const gpdId = templatesHashRef.current.get(id)?.overridenTemplateId
    setGpdTemplateId(gpdId)
  }

  useEffect(() => {
    try {
      const id = +activeTemplateParamId
      if (id && templatesData?.length && templatesHash.size) {
        selectTemplate(id, '')
      }
    } catch (e) {
      console.error(e)
    }
  }, [activeTemplateParamId, templatesData, templatesHash])

  useEffect(() => {
    resetAddedDataFields()
  }, [activeTemplate])

  useEffect(() => {
    if (newProcessorId) {
      refetchTemplates().then(() => {
        selectTemplate(newProcessorId, '')
      })
    }
  }, [newProcessorId])

  useEffect(() => {
    selectTemplate(null, '')
  }, [selectedMerchant, selectedProcessor])

  const createTemplate = async (
    originTemplate: TemplateDTO,
    newTemplateData: Partial<TemplateDTO>
  ) => {
    setIsGeneralLoading(true)
    try {
      await createNewTemplate(originTemplate, newTemplateData)

      if (
        originTemplate.merchantId || // override template
        !selectedMerchant || // only gpd is visible
        selectedMerchant.id === NONE_MERCHANT.id
      ) {
        await refetchRelevantTemplate()
      } else {
        // both gpd and merchant are visible, we need to re-fetch only gpd
        await refetchGpdRelevantTemplate()
      }
      await refetchTemplates()
    } catch (e) {
      console.error(e)
    }
    setIsGeneralLoading(false)
  }

  const removeTemplate = async (
    id: number,
    newActiveTemplateId?: number,
    parent?: string
  ) => {
    setIsGeneralLoading(true)

    try {
      const template = templatesHash.get(id)
      const isGpd = !template?.overridenTemplateId && !template.merchantSpecific
      await deleteTemplate(id, isGpd)

      if (newActiveTemplateId) {
        selectTemplate(newActiveTemplateId, parent || '')
      } else {
        selectTemplate(null, '')
      }

      await refetchTemplates()
      setIsGeneralLoading(false)
    } catch (e) {
      setIsGeneralLoading(false)
      console.error(e)
    }
  }

  const toggleTemplateHiddenStatus = async (id: number) => {
    setIsGeneralLoading(true)

    try {
      const {
        data: { id: templateId }
      } = await changeTemplateHiddenStatus(id, selectedMerchant.id)
      await refetchTemplates()
      await refetchTemplate()
      if (templateId !== activeTemplateData.templateId) {
        selectTemplate(templateId, parentList)
      }
      setIsGeneralLoading(false)
    } catch (e) {
      setIsGeneralLoading(false)
      console.error(e)
    }
  }

  const toggleTemplateRenderingExcludedStatus = async (id: number) => {
    setIsGeneralLoading(true)

    try {
      const {
        data: { id: templateId }
      } = await changeTemplateRenderingExcludedStatus(id, selectedMerchant.id)
      await refetchTemplates()
      await refetchTemplate()
      if (templateId !== activeTemplateData.templateId) {
        selectTemplate(templateId, parentList)
      }
      setIsGeneralLoading(false)
    } catch (e) {
      setIsGeneralLoading(false)
      console.error(e)
    }
  }

  const onRevertOverride = () => {
    removeTemplate(
      activeTemplate,
      activeTemplateData.overridenTemplateId,
      parentList
    )
  }

  const saveTemplateChanges = async () => {
    setIsGeneralLoading(true)
    try {
      const payload: {
        content?: string
        // eslint-disable-next-line camelcase
        data_fields_attributes?: any[]
        // eslint-disable-next-line camelcase
        display_name?: string
      } = {}

      if (updatedTemplateContent?.isDirty) {
        let newContent = ''
        if (updatedTemplateContent.rawHtmlContent) {
          newContent = updatedTemplateContent.rawHtmlContent
        } else {
          const newHtml = updatedTemplateContent.editor.getHTML()
          newContent = postprocessContentForBackend(newHtml)
        }
        payload.content = newContent
        payload.data_fields_attributes = addedDataFields
      }

      payload.display_name = updatedTemplateName

      await putTemplate(activeTemplate, payload)
      setUpdatedTemplateContent(prevState => ({ ...prevState, isDirty: false }))
      setUpdatedTemplateName(null)
      await refetchTemplates()
      resetAddedDataFields()
      await refetchTemplate()
      await refetchDataFields()
    } catch (e) {
      console.error(e)
    }
    setIsGeneralLoading(false)
  }

  const overrideTemplate = async (template: TemplateDTO) => {
    try {
      if (
        selectedMerchant.id &&
        selectedMerchant.id !== NONE_MERCHANT.id &&
        template?.templateId
      ) {
        setIsGeneralLoading(true)
        const { data: overriddenTemplate } = await callOverrideTemplate(
          template,
          selectedMerchant.id
        )
        // Navigate before refetching so the URL-sync effect (which fires when
        // templatesData changes) sees the new template ID in the URL instead of
        // the old one, preventing it from snapping back to the processor default.
        selectTemplate(overriddenTemplate.templateId, parentList)
        await refetchTemplates()
        setIsGeneralLoading(false)
      }
    } catch (e) {
      console.error(e)
      setIsGeneralLoading(false)
    }
  }

  const saveEvidenceRules = async (rules: EvidenceRule, isNew: boolean) => {
    if (rules) {
      try {
        setIsGeneralLoading(true)
        const { data: rule } = await postEvidenceRules(rules, isNew)
        setUpdatedEvidenceRules(rule)
        setIsGeneralLoading(false)
        refetchTemplate()
      } catch (e) {
        console.error(e)
        setIsGeneralLoading(false)
      }
    }
  }

  const generateRules = async (description: string) => {
    try {
      setIsGeneralLoading(true)
      const rules = await generateJsonLogic(description)
      setIsGeneralLoading(false)
      return rules?.data
    } catch (e) {
      console.error(e)
      setIsGeneralLoading(false)
      return null
    }
  }

  const onToggleActiveStatus = async () => {
    if (!activeTemplateData?.overridenTemplateId) {
      return
    }

    try {
      setIsGeneralLoading(true)
      await patchToggleActive(activeTemplate, !activeTemplateData.active)
      await refetchTemplate()
      setIsGeneralLoading(false)
    } catch (e) {
      console.error(e)
      setIsGeneralLoading(false)
    }
  }

  const toggleCollapse = () => {
    setCollapsedState(!collapsedState)
  }

  const isLoading =
    isTemplatesLoading ||
    isTemplateLoading ||
    isGpdTemplateLoading ||
    isGeneralLoading ||
    isJsonConfigLoading ||
    isRelevantTemplateLoading ||
    isGpdRelevantTemplateLoading ||
    isProcessorsLoading ||
    isAddingProcessorField

  const relevantTemplates = {
    [activeTemplate]: activeRelevantTemplates,
    [gpdTemplateId]: gpdRelevantTemplates
  }

  const contextValue = useMemo(
    () => ({
      templateList: templatesData,
      isTemplatesLoading,
      rootTemplates,
      templatesHash,
      activeTemplate,
      selectTemplate,
      searchValue,
      setSearchValue,
      parentList,
      activeTemplateData,
      updatedEvidenceRules,
      setUpdatedEvidenceRules,
      saveTemplateChanges,
      createTemplate,
      removeTemplate,
      updatedTemplateName,
      setUpdatedTemplateName,
      isLoading,
      generateRules,
      saveEvidenceRules,
      jsonConfig,
      overrideTemplate,
      gpdTemplateData,
      onRevertOverride,
      relevantTemplates,
      updatedTemplateContent,
      setUpdatedTemplateContent,
      collapsedState,
      toggleCollapse,
      onToggleActiveStatus,
      toggleTemplateHiddenStatus,
      toggleTemplateRenderingExcludedStatus
    }),
    [
      templatesData,
      isTemplatesLoading,
      rootTemplates,
      templatesHash,
      activeTemplate,
      selectTemplate,
      searchValue,
      setSearchValue,
      parentList,
      activeTemplateData,
      updatedEvidenceRules,
      setUpdatedEvidenceRules,
      saveTemplateChanges,
      createTemplate,
      removeTemplate,
      updatedTemplateName,
      setUpdatedTemplateName,
      isLoading,
      generateRules,
      saveEvidenceRules,
      jsonConfig,
      overrideTemplate,
      gpdTemplateData,
      onRevertOverride,
      relevantTemplates,
      updatedTemplateContent,
      setUpdatedTemplateContent,
      collapsedState,
      toggleCollapse,
      onToggleActiveStatus,
      toggleTemplateHiddenStatus,
      toggleTemplateRenderingExcludedStatus
    ]
  )

  return (
    <TemplateContext.Provider value={contextValue}>
      {children}
    </TemplateContext.Provider>
  )
}

export const useTemplateContext = () => {
  return useContext(TemplateContext)
}
